import { getFromLS, supportsMacAACSecurity } from '@/lib/commonHelpers';
import { message } from '@/lib/notify/apNotify';
import {
  AppType,
  checkNativeMethodSupported,
  getWhiteListUrls,
  macAppsToClose,
} from '@/lib/offline/OfflineAppHelper';
import { MethodExecutionResult, useOfflineAppStore } from '@/store/offlineAppStore';
import { useTranslate } from '@refinedev/core';
import React, { useCallback } from 'react';

// PRELOADED at module scope (lockdown flows must not need a mid-exam fetch).
// R16: the blocking alert is the native apDialog (was the antd UIHelper modal);
// QuitButton stays a dynamic import (breaks the hook↔button cycle).
const apDialogLoad = import('@/components/ap/confirm/APDialog');
const quitBtnLoad = import('@/components/ap/lockdown/QuitButton');

// Inlined from the retired v1 ExperienceShow (R15b-b3).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ExperienceSettingsProps = any;

export interface MethodRequest {
  key: string;
  value?: any;
}

export const changeSecurityFailedAlert = (
  isChangeSecurityFailed: boolean,
  translate: any,
  appType: AppType,
  executeMethods: (methods: MethodRequest[]) => Promise<any>
) => {
  if (getFromLS('securityHasBeenInterrupted')) {
    return;
  }

  console.log('checkNativeMethodSupported', checkNativeMethodSupported(appType, 'showQuitAlert'));

  if (checkNativeMethodSupported(appType, 'showQuitAlert')) {
    executeMethods([
      {
        key: 'showQuitAlert',
        value:
          "Security couldn't be turned on, and the test cannot be started. In case this issue continues, please restart your machine and try again.",
      },
    ]);
  } else {
    // Chunks are preloaded at module scope — this resolves from cache.
    void Promise.all([apDialogLoad, quitBtnLoad]).then(([apDialog, quitBtn]) => {
      apDialog.showBlockingDialog({
        title: isChangeSecurityFailed
          ? translate('ExperienceTake.security_could_not_be_turned_on')
          : translate('ExperienceTake.security_could_not_be_turned_off'),
        content: isChangeSecurityFailed
          ? translate('ExperienceTake.change_security_failed_message_for_turning_on')
          : translate('ExperienceTake.change_security_failed_message_for_turning_off'),
        footer: React.createElement(quitBtn.QuitButton, {
          quitButtonText: translate('CommonText.quit'),
        }),
      });
    });
  }
};
export const changeSecurityFailedMessage = (translate: any) => {
  message.error(translate('ExperienceTake.change_security_failed_message_for_turning_on'));
};

export const useOfflineAppMethods = (methods?: MethodRequest | MethodRequest[]) => {
  const {
    executeMethods,
    methodResults,
    loading,
    error,
    clearMethodResults: clearMethodResultsStore,
  } = useOfflineAppStore();
  const translate = useTranslate();

  /**
   * Enables lockdown mode for the app based on app type and experience settings. lockdown App available for mac, ios, seb, chromebook.
   * @param appType - The type of app (mac, ios, seb, cros, web, win, chromebook, etc)
   * @param experienceSettings - Settings for the experience/test
   * @returns Promise<boolean> - Whether lockdown was successfully enabled
   * @note This method is used to enable lockdown mode for the app based on app type and experience settings.
   */
  const enableLockdownApp = useCallback(
    async (
      appType: AppType,
      experienceSettings: ExperienceSettingsProps,
      options?: {
        onSuccess?: () => void;
        onError?: (error?: any) => void;
        shouldClearClipboard?: boolean;
        changeSecurityTo?: boolean;
      }
    ) => {
      const { onSuccess, onError, shouldClearClipboard, changeSecurityTo = true } = options || {};
      if (changeSecurityTo) {
        console.log('checking mac aac security', appType);
        const whitelistUrls = getWhiteListUrls(experienceSettings);

        // Handle Mac AAC security
        if (supportsMacAACSecurity(appType)) {
          // Check if getSecurityStatus method is supported
          if (checkNativeMethodSupported(appType, 'getSecurityStatus')) {
            try {
              // Get current security status
              const securityResponse = await executeMethods([
                { key: 'getSecurityStatus', value: 'status' },
              ]);
              console.log('result getSecurityStatus', securityResponse);

              if (securityResponse?.getSecurityStatus === 'true') {
                // Security already enabled, just update whitelist
                try {
                  const methods: any = [
                    {
                      key: 'setWhitelistedUrls',
                      value: whitelistUrls,
                      onError: () => {
                        console.error('Error in setting whitelist urls', whitelistUrls);
                        message.error(
                          translate('CheckAndChangeSecurity.whitelist_urls_could_not_be_set')
                        );
                      },
                    },
                  ];
                  // Clear the clipboard after clicking Join Test when shouldClearClipboard is enabled to prevent pasting during lockdown.
                  if (shouldClearClipboard) {
                    methods.push({ key: 'clearClipboard', value: true });
                  }
                  const response = await executeMethods(methods);
                  onSuccess?.();
                  console.log('result setWhitelistedUrls', response);

                  return true;
                } catch (error: unknown) {
                  console.error('Error setting whitelisted urls:', error);
                  message.error('Error setting whitelisted urls setWhitelistedUrls');
                  return false;
                }
              } else {
                // Enable security and set whitelist
                try {
                  const securityChangeResponse = await executeMethods([
                    {
                      key: 'changeSecurity',
                      value: true,
                      onSuccess: (value) => {
                        if (value === 'true') {
                          onSuccess?.();
                        } else {
                          onError?.(value);
                        }
                      },
                      onError: (error) => {
                        onError?.(error);
                        // changeSecurityFailedAlert(true, translate, appType, executeMethods);
                      },
                    },
                    {
                      key: 'setWhitelistedUrls',
                      value: whitelistUrls,
                    },
                  ]);

                  if (securityChangeResponse?.changeSecurity !== 'true') {
                    return false;
                  }
                  console.log('result changeSecurity', securityChangeResponse);
                  return true;
                } catch (error) {
                  return false;
                }
              }
            } catch (error) {
              console.error('Error getting security status getSecurityStatus', error);
              return false;
            }
          } else {
            // Fallback for when getSecurityStatus not supported
            try {
              const response = await executeMethods([
                {
                  key: 'changeSecurity',
                  value: true,
                  onError: () => {
                    onError?.();
                    // changeSecurityFailedAlert(true, translate, appType, executeMethods);
                  },
                },
                { key: 'setWhitelistedUrls', value: whitelistUrls },
              ]);

              if (response?.changeSecurity === 'true') {
                message.success('Security changed successfully');
              } else {
                onError?.();
                // changeSecurityFailedAlert(true, translate, appType, executeMethods);
              }
              return true;
            } catch (error) {
              return false;
            }
          }
        } else {
          // Handle non-Mac AAC security
          await executeMethods([
            {
              key: 'changeSecurity',
              value: true,
              onError: () => {
                changeSecurityFailedAlert(true, translate, appType, executeMethods);
              },
            },
            { key: 'clearClipboard', value: true },
            { key: 'closeAllApps', value: macAppsToClose },
            { key: 'setWhitelistedUrls', value: whitelistUrls },
          ]);
        }
        return false;
      } else {
        const response = await executeMethods([
          {
            key: 'changeSecurity',
            value: false,
            onSuccess: (value) => {
              if (value === 'true') {
                onSuccess?.();
              } else {
                onError?.(value);
              }
            },
            onError: (error) => {
              onError?.(error);
              changeSecurityFailedAlert(false, translate, appType, executeMethods);
              // changeSecurityFailedAlert(true, translate, appType, executeMethods);
            },
          },
        ]);
        return response;
      }
    },
    [executeMethods]
  );

  const getMethodResult = useCallback(
    (methodKey: string): MethodExecutionResult | undefined => {
      return methodResults[methodKey];
    },
    [methodResults]
  );

  const getMethodResults = useCallback(
    (methodKeys?: string[]): Record<string, MethodExecutionResult> => {
      if (!methodKeys) return methodResults;
      return methodKeys.reduce((acc, key) => {
        if (methodResults[key]) {
          acc[key] = methodResults[key];
        }
        return acc;
      }, {} as Record<string, MethodExecutionResult>);
    },
    [methodResults]
  );

  const clearMethodResults = useCallback(() => {
    clearMethodResultsStore();
  }, [clearMethodResultsStore]);

  return {
    executeMethod: executeMethods,
    executeMethods: executeMethods,
    getMethodResult,
    getMethodResults,
    clearMethodResults,
    methodResults: methodResults,
    isLoading: loading,
    error: error,
    enableLockdownApp: enableLockdownApp,
  };
};
